import React from 'react';
import moment from 'moment';
import type { CalendarComponentEvent, TimeZoneIana } from '@axiom/validation';

import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { Gutter } from '../../layout/Gutter/Gutter';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { DateUtil } from '../../../utils/date-util';
import { CalendarTimeZoneUtil } from '../../../utils/calendar-time-zone-util';
import { Modal } from '../../element/Modal/Modal';
import { ModalSection } from '../../element/Modal/ModalSection';
import { Icon } from '../../element/Icon/Icon';
import { ModalFooter } from '../../element/Modal/ModalFooter';
import { Button } from '../../element/Button/Button';
import { ModalHeader } from '../../element/Modal/ModalHeader';
import { Banner, BannerImpact, BannerType } from '../../element/Banner/Banner';

export interface CalendarDeleteConfirmationModalProps {
  onClose: () => void;
  deleteEvent: CalendarComponentEvent;
  onDelete: () => void;
}

export const CalendarDeleteConfirmationModal = ({
  onClose,
  deleteEvent,
  onDelete,
}: CalendarDeleteConfirmationModalProps) => {
  const timezone = CalendarTimeZoneUtil.getZoneFromMomentZone(
    moment.tz(new Date(), moment(deleteEvent.start).tz() as TimeZoneIana)
  );
  return (
    <Modal name="DELETE_CONFIRMATION_MODAL">
      <ModalHeader
        onClose={() => {
          onClose();
        }}
        name="DELETE_CONFIRMATION_HEADER"
      >
        Delete Interview
      </ModalHeader>
      <ModalSection>
        <Banner type={BannerType.ERROR} impact={BannerImpact.HIGH}>
          Deleted interviews can only be rescheduled manually and talent will
          remain in interview status.
        </Banner>
        <Gutter vertical="8px" />
        <Paragraph>
          The event below will be deleted and all attendees will receive an
          interview cancellation notice.
        </Paragraph>
        <Gutter vertical="8px" />
        {!!deleteEvent.accountName && (
          <>
            <ParagraphHeader name="DELETE_CONFIRM_MODAL_ACCOUNT_NAME">
              {deleteEvent.accountName}
            </ParagraphHeader>
            <Gutter bottom="16px" />
          </>
        )}
        {(deleteEvent.attendees?.length ?? 0) > 0 && (
          <>
            <Layout position="top" horizontalGutter="8px">
              <LayoutItem>
                <Icon name="avatar" />
              </LayoutItem>
              <LayoutItem>
                {deleteEvent.attendees?.map((attendee, index) => (
                  <Paragraph
                    name="ATTENDEE"
                    key={`${attendee.email}_${index + 1}`}
                  >
                    {`${attendee.name} (${attendee.email})`}
                  </Paragraph>
                ))}
              </LayoutItem>
            </Layout>
            <Gutter vertical="8px" />
          </>
        )}
        <Layout position="middle" horizontalGutter="8px">
          <Icon name="calendar" />
          <Paragraph name="EVENT_DATE">{`${DateUtil.displayLongDay(
            deleteEvent.start
          )}, ${DateUtil.displayLongMonthDayYear(
            deleteEvent.start
          )}`}</Paragraph>
        </Layout>
        <Gutter vertical="8px" />
        <Layout position="middle" horizontalGutter="8px">
          <Icon name="clock" />
          <Paragraph name="EVENT_TIME">{`${DateUtil.displayFullTimeRange(
            deleteEvent.start,
            deleteEvent.end
          )} ${timezone?.abbr}`}</Paragraph>
        </Layout>
      </ModalSection>
      <ModalFooter>
        <Button
          name="CANCEL_BUTTON"
          variation="outline"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          name="DELETE_BUTTON_CONFIRMATION"
          onClick={() => {
            onDelete();
          }}
        >
          Delete
        </Button>
      </ModalFooter>
    </Modal>
  );
};
